/* eslint-disable react/forbid-prop-types */

import React, { cloneElement, isValidElement, PureComponent, Children } from 'react';
import { has, memoize } from 'lodash-es';
import PropTypes from 'prop-types';
import { composeValidators } from '@upgrade/ui-utils/validators';
import { withFormMessage } from '../FormMessage';
import findByType from '../utils/find-by-type';
import FormErrorMessage from './submission-status';
import { getLabelId } from '../utils/aria';
const defaultMapInputProps = ({
  inputProps
}) => inputProps;
const defaultMapEventToValue = ({
  target: {
    value
  }
}) => value;
const defaultValidate = () => ({});
export const hasErrors = (errors = {}) => {
  return Object.keys(errors).length > 0;
};
export const Placeholder = () => null;
export const hasErrorMessage = children => findByType(children, FormErrorMessage) !== null;
export const FormContext = /*#__PURE__*/React.createContext({});
export default class Form extends PureComponent {
  static propTypes = {
    initialValues: PropTypes.object.isRequired,
    validators: PropTypes.object,
    validate: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    children: PropTypes.node.isRequired,
    // eslint-disable-line react/no-unused-prop-types
    errors: PropTypes.object,
    // eslint-disable-line react/no-unused-prop-types
    disabled: PropTypes.bool,
    // Title is used by screen readers to give context to the user once the focus has
    // been moved inside any form field.
    title: PropTypes.string.isRequired,
    // Feature Flag to enable accessibility
    accessible: PropTypes.bool
  };
  static defaultProps = {
    errors: {},
    validators: {},
    onChange: () => {},
    validate: null,
    disabled: false,
    accessible: false
  };
  constructor(props) {
    super(props);
    const {
      initialValues
    } = this.props;
    this.state = {
      submitTimes: 0,
      touched: false,
      formValues: {
        ...initialValues
      }
    };
    this.errorNotificationId = getLabelId('form-error');
  }
  static getDerivedStateFromProps({
    errors
  }, {
    errors: oldErrors,
    touched: oldTouched
  }) {
    return {
      touched: oldTouched || !!(errors && Object.keys(errors).length),
      errors: {
        ...oldErrors,
        ...errors
      }
    };
  }
  getExposedFormState() {
    const {
      formValues,
      touched,
      errors
    } = this.state;
    return {
      formValues,
      touched,
      errors
    };
  }
  validate(...args) {
    const {
      validate,
      validators
    } = this.props;
    const fn = validate || (validators ? composeValidators(validators) : defaultValidate);
    return fn(...args);
  }
  setFormValue({
    name,
    value
  }) {
    this.setState(({
      formValues: oldFormValues
    }) => {
      const formValues = {
        ...oldFormValues,
        [name]: value
      };
      const errors = this.validate(formValues);
      return {
        formValues,
        errors
      };
    }, () => {
      const {
        formValues,
        errors
      } = this.state;
      const {
        onChange
      } = this.props;
      onChange(formValues, name, errors);
    });
  }
  mapInjectedNodeType = memoize(({
    type,
    type: {
      injectMessages
    }
  }) => {
    if (injectMessages) {
      return withFormMessage()(type);
    }

    // Replace the placeholder with the correct component
    if (type === Placeholder) {
      return FormErrorMessage;
    }
    return type;
  }, ({
    type
  }) => type);
  handleChange = ({
    name,
    mapFieldValue,
    originalOnChange
  }) => (...args) => {
    const value = mapFieldValue(...args);
    if (typeof originalOnChange === 'function') {
      originalOnChange(...args);
    }
    this.setFormValue({
      name,
      value
    });
  };
  handleSubmit = event => {
    event.preventDefault();
    const {
      formValues,
      submitTimes
    } = this.state;
    const {
      onSubmit,
      disabled
    } = this.props;
    if (disabled) {
      event.stopPropagation();
      return;
    }
    const errors = this.validate(formValues);
    if (Object.keys(errors).length) {
      // need to stopPropagation in case we have nested forms (eg. Modal) since events from portals propagate according
      // to the React tree rather than the DOM tree
      event.stopPropagation();
      this.setState({
        submitTimes: submitTimes + 1,
        touched: true,
        errors
      });
      return;
    }
    onSubmit({
      event,
      ...this.getExposedFormState()
    });
  };
  injectFormField = ({
    type: {
      mapFormProps,
      mapFieldValue = defaultMapEventToValue
    },
    props
  }) => {
    const {
      name,
      disabled,
      mapFormState,
      onChange: originalOnChange
    } = props;
    const formState = this.getExposedFormState();
    const {
      formValues,
      touched,
      errors
    } = formState;
    if (!has(formValues, name)) {
      return {};
    }
    const {
      disabled: formDisabled
    } = this.props;
    const value = formValues[name];
    const error = errors[name];
    const inputProps = {
      value,
      error: touched && error || null,
      onChange: this.handleChange({
        name,
        mapFieldValue,
        originalOnChange
      }),
      disabled: formDisabled || disabled
    };
    const newProps = (mapFormProps || defaultMapInputProps)({
      props,
      formState,
      inputProps,
      defaultMapInputProps
    });
    if (mapFormState) {
      Object.assign(newProps, mapFormState({
        ...formState,
        value,
        name,
        error
      }));
    }
    return newProps;
  };
  injectFormControl = ({
    type: {
      injectFormProps
    },
    props: {
      disabled
    }
  }) => {
    if (!injectFormProps) {
      return {};
    }
    return {
      disabled: this.props.disabled || disabled
    };
  };
  injectChildren = ({
    props: {
      name,
      children
    }
  }) => {
    if (name || !Children.count(children)) {
      return {};
    }
    return {
      children: Children.map(children, this.injectNode)
    };
  };
  injectPlaceholder = ({
    type
  }) => {
    const {
      errors,
      touched
    } = this.getExposedFormState();
    const {
      submitTimes
    } = this.state;
    const {
      accessible
    } = this.props;
    if (accessible && type === Placeholder) return {
      id: this.errorNotificationId,
      hasErrors: touched && hasErrors(errors),
      submitTimes
    };
    return {};
  };
  injectNode = node => {
    if (! /*#__PURE__*/isValidElement(node)) {
      return node;
    }
    const type = this.mapInjectedNodeType(node);
    const injectedProps = [this.injectPlaceholder, this.injectFormControl, this.injectFormField, this.injectChildren].map(inject => inject(node)).reduce((acc, injected) => ({
      ...acc,
      ...injected
    }), {});
    const newNodeDef = {
      ...node,
      type
    };
    const injectedNode = Object.keys(injectedProps).length ? /*#__PURE__*/cloneElement(newNodeDef, injectedProps) : newNodeDef;
    return injectedNode;
  };
  render() {
    const {
      errors,
      touched
    } = this.getExposedFormState();
    const {
      accessible
    } = this.props;
    const {
      submitTimes
    } = this.state;
    const nowHasErrors = touched && hasErrors(errors);
    const {
      title,
      id
    } = this.props;
    const {
      children
    } = this.injectChildren(this);
    const hasErrorMessageDefined = hasErrorMessage(children);
    const formProps = {
      className: 'form',
      'aria-label': title,
      onSubmit: this.handleSubmit,
      id
    };

    // We want consumers to rerender when Form renders just like direct children would.
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const providerValue = {
      injectNode: this.injectNode
    };
    return /*#__PURE__*/React.createElement(FormContext.Provider, {
      value: providerValue
    }, /*#__PURE__*/React.createElement("form", formProps, accessible && !hasErrorMessageDefined ? /*#__PURE__*/React.createElement(FormErrorMessage, {
      id: this.errorNotificationId,
      hasErrors: nowHasErrors,
      submitTimes
    }) : null, children));
  }
}
//# sourceMappingURL=index.js.map